import type { GameMode } from '../types/gameMode.types';
import type { BlockStimulus, StimulusType } from '../types/payloads/stimulus.types';
import { inferStimulusType, parseBlockStimulus } from '../schemas/stimulusSchema';
import { inferStimulusTheme } from './stimulusTheme';

const BLIND_REFERENCE_RES: RegExp[] = [
  /\b(el poema|la poema|el fragmento|la estrofa|el verso|el texto|la carta|el documento|la fuente|la noticia|el relato|la historia|la crónica)\b/i,
  /\b(según el texto anterior|según la noticia|según el poema|según el fragmento|según la fuente|según el relato)\b/i,
  /\b(en el poema|en la noticia|en el fragmento|en la estrofa|en el relato|en la historia|en la crónica)\b/i,
  /\b(lee(?:r)?\s+(?:atentamente\s+)?(?:el|la|los|las|este|esta|la\s+siguiente|el\s+siguiente)\s+(?:texto|noticia|poema|fragmento|historia|relato|crónica))\b/i,
  /\b(texto|noticia|poema|fragmento|historia|relato)\s+siguiente\b/i,
  /\b(recursos poéticos|recurso poético)\b/i,
  /\b(analiza(?:r)?|identifica(?:r)?|interpreta(?:r)?|completa(?:r)?)\s+(?:el|la|los|las|en)\s+(?:texto|poema|noticia|fragmento|historia|relato)\b/i,
  /«[^»]{4,}»|"[^"]{4,}"|'[^']{4,}'/,
  /\b(?:según|sobre|del|de la|en|para)\s+[A-ZÁÉÍÓÚÑ][\wáéíóúñ]+(?:\s+(?:de|del|la|el|San|Santa|Los|Las)\s+)?[A-ZÁÉÍÓÚÑ][\wáéíóúñ]+(?:\s+[A-ZÁÉÍÓÚÑ][\wáéíóúñ]+)?\b/,
];

function matchesBlindReference(text: string): boolean {
  return BLIND_REFERENCE_RES.some((re) => re.test(text));
}

const QUOTED_WORK_RE = /[«""']([^«""']{4,})[«""']/;

function payloadRecord(payload: unknown): Record<string, unknown> {
  return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
}

function stripInlineMarkdown(text: string): string {
  return String(text || '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .trim();
}

export function collectPayloadInstructionTexts(payload: Record<string, unknown>, mode: GameMode): string[] {
  const texts: string[] = [];
  const push = (value: unknown) => {
    const text = String(value || '').trim();
    if (text) texts.push(text);
  };

  push(payload.title);
  push(payload.prompt);
  push(payload.text);
  push(payload.printTitle);
  push(payload.context);
  push(payload.instructionText);

  if (mode === 'arena' && Array.isArray(payload.questions)) {
    for (const question of payload.questions) {
      if (question && typeof question === 'object') {
        push((question as { prompt?: string }).prompt);
      }
    }
  }

  return texts;
}

export function detectBlindStimulusReference(payload: Record<string, unknown>, mode: GameMode): boolean {
  if (parseBlockStimulus(payload.stimulus)?.content) return false;
  const legacySource = String(payload.sourceText || payload.modelAnswer || '').trim();
  if (legacySource.length >= 12) return false;
  const longContext = String(payload.context || '').trim();
  if (longContext.length >= 80) return false;

  return collectPayloadInstructionTexts(payload, mode).some((text) => matchesBlindReference(text));
}

/** Modos editoriales puros — no llevan contrato de estímulo evaluativo */
export const STIMULUS_EXEMPT_MODES = new Set<GameMode>([
  'subtitulo',
  'destacado',
  'figura',
  'glosario_tabla',
  'formula_destacada',
]);

export function supportsBlockStimulus(mode: GameMode): boolean {
  return !STIMULUS_EXEMPT_MODES.has(mode);
}

export function migrateLegacyStimulusFields(payload: Record<string, unknown>): BlockStimulus | undefined {
  const existing = parseBlockStimulus(payload.stimulus);
  if (existing) return existing;

  const sourceText = stripInlineMarkdown(
    String(payload.sourceText || payload.modelAnswer || '').trim(),
  );
  if (sourceText.length >= 12) {
    return {
      title: String(payload.stimulusTitle || payload.title || '').trim() || undefined,
      type: inferStimulusType(sourceText, String(payload.prompt || payload.text || '')),
      content: sourceText,
    };
  }

  const context = stripInlineMarkdown(String(payload.context || '').trim());
  if (context.length >= 80) {
    return {
      title: String(payload.stimulusTitle || payload.title || '').trim() || undefined,
      type: inferStimulusType(context, String(payload.prompt || payload.text || '')),
      content: context,
    };
  }

  return undefined;
}

export function normalizeBlockStimulus(
  payload: Record<string, unknown>,
  mode: GameMode,
): Record<string, unknown> {
  const next = { ...payload };
  let stimulus = migrateLegacyStimulusFields(next) ?? parseBlockStimulus(next.stimulus);

  if (stimulus) {
    const hint = collectPayloadInstructionTexts(next, mode).join(' ');
    const type = stimulus.type || inferStimulusType(stimulus.content, hint);
    stimulus = {
      title: stimulus.title ? stripInlineMarkdown(stimulus.title) : undefined,
      type,
      content: stripInlineMarkdown(stimulus.content),
      theme: stimulus.theme || inferStimulusTheme(type, stimulus.content, stimulus.title ?? hint),
      ...(stimulus.imagePrompt ? { imagePrompt: String(stimulus.imagePrompt).trim() } : {}),
      ...(stimulus.wikiQuery ? { wikiQuery: String(stimulus.wikiQuery).trim() } : {}),
      ...(stimulus.imageUrl ? { imageUrl: String(stimulus.imageUrl).trim() } : {}),
    };
    next.stimulus = stimulus;
  } else {
    delete next.stimulus;
  }

  if (next.imagePrompt != null) next.imagePrompt = String(next.imagePrompt).trim() || undefined;
  if (next.wikiQuery != null) next.wikiQuery = String(next.wikiQuery).trim() || undefined;
  if (next.imageUrl != null) next.imageUrl = String(next.imageUrl).trim() || undefined;

  if (next.sourceText != null) delete next.sourceText;
  if (next.modelAnswer != null) delete next.modelAnswer;

  return next;
}

export function applyStimulusFields<T extends Record<string, unknown>>(
  validated: T,
  raw: unknown,
  mode: GameMode,
): T {
  const rawData = payloadRecord(raw);
  const normalized = normalizeBlockStimulus(
    {
      ...validated,
      stimulus: rawData.stimulus,
      sourceText: rawData.sourceText,
      modelAnswer: rawData.modelAnswer,
      context: validated.context ?? rawData.context,
      stimulusTitle: rawData.stimulusTitle,
    },
    mode,
  );

  const out = { ...validated } as T & { stimulus?: BlockStimulus; context?: string };
  if (normalized.stimulus) {
    (out as { stimulus?: BlockStimulus }).stimulus = normalized.stimulus as BlockStimulus;
    const ctx = String(validated.context || '').trim();
    if (ctx && (normalized.stimulus as BlockStimulus).content === ctx) {
      delete (out as { context?: string }).context;
    }
  }
  return out as T;
}

export function resolvePrintStimulus(payload: Record<string, unknown>, mode: GameMode): BlockStimulus | undefined {
  const normalized = normalizeBlockStimulus(payload, mode);
  return parseBlockStimulus(normalized.stimulus);
}

export function normalizeGuideStimulusBlocks<T extends { blocks: Array<{ mode: GameMode; payload: unknown }> }>(
  exam: T,
): T {
  const blocks = exam.blocks.map((block) => {
    const payload = payloadRecord(block.payload);
    const normalizedPayload = normalizeBlockStimulus(payload, block.mode);
    if (normalizedPayload === payload) return block;
    return { ...block, payload: normalizedPayload };
  });
  return blocks === exam.blocks ? exam : { ...exam, blocks };
}

export function stimulusContractError(payload: Record<string, unknown>, mode: GameMode): string | null {
  if (!detectBlindStimulusReference(payload, mode)) return null;
  return 'stimulus_required: el ejercicio cita un texto específico sin payload.stimulus.content';
}
